import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  BULK_ACTIVITIES_QUERY_KEYS,
  BULK_OPERATION_QUEUE_API_SERVICES,
} from './api';
import {
  BulkActivitiesResponse,
  BulkActivityItem,
} from './BulkOperationQueue.interface';

interface UseBulkActivitiesPollingOptions {
  /** Whether to automatically start polling (default: true) */
  autoFetch?: boolean;
  /** Polling interval in milliseconds (default: 5000ms = 5 seconds) */
  pollInterval?: number;
  /** Maximum number of retries before stopping (default: 100) */
  maxRetries?: number;
  /** Callback when all activities complete */
  onAllComplete?: (activities: BulkActivityItem[]) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

interface UseBulkActivitiesPollingResult {
  /** Whether the query is currently loading */
  isLoading: boolean;
  /** Whether polling is active */
  isPolling: boolean;
  /** The bulk activities data */
  data: BulkActivitiesResponse | undefined;
  /** Active (in-progress) activities */
  activeActivities: BulkActivityItem[];
  /** Completed activities */
  completedActivities: BulkActivityItem[];
  /** Failed activities */
  failedActivities: BulkActivityItem[];
  /** Whether there are any active activities */
  hasActiveActivities: boolean;
  /** Whether max retries was exceeded */
  isMaxRetriesExceeded: boolean;
  /** Manually trigger a fetch/refetch */
  triggerFetch: () => void;
  /** Stop polling */
  stopPolling: () => void;
  /** Error if any */
  error: Error | null;
}

/**
 * Hook for polling bulk activities every 5 seconds until all are complete.
 * Similar to useSharedCalendarPolling but for bulk operations.
 */
// Query function that always passes skip_viewed: true
const fetchBulkActivitiesWithSkipViewed =
  async (): Promise<BulkActivitiesResponse> => {
    return await BULK_OPERATION_QUEUE_API_SERVICES.getBulkActivities({
      skip_viewed: true,
    });
  };

export const useBulkActivitiesPolling = ({
  autoFetch = true,
  pollInterval = 5000, // 5 seconds
  maxRetries = 100,
  onAllComplete,
  onError,
}: UseBulkActivitiesPollingOptions = {}): UseBulkActivitiesPollingResult => {
  const queryClient = useQueryClient();
  const [isTriggered, setIsTriggered] = useState(autoFetch);
  const [isMaxRetriesExceeded, setIsMaxRetriesExceeded] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(true);

  const pollCount = useRef(0);
  const hasCalledOnAllComplete = useRef(false);
  const allActivitiesRef = useRef<BulkActivityItem[]>([]);
  const recentlyTriggeredRef = useRef(false);
  const gracePeriodTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Flag to prevent firing completion callback on stale data right after triggering
  const waitingForFreshData = useRef(false);
  // Set once work was seen in flight (an active activity, or a trigger after
  // the user started one). The first poll on page load returns finished but
  // unviewed activities too; without this the completion event fired on every
  // load and each listening list refetched data it had only just fetched.
  const hasSeenWork = useRef(false);

  // Sync isTriggered with autoFetch when it changes (e.g., when isAuthenticated becomes true)
  useEffect(() => {
    if (autoFetch && !isTriggered) {
      setIsTriggered(true);
    }
  }, [autoFetch, isTriggered]);

  // Reset states for new fetch
  const resetStates = useCallback(() => {
    setIsMaxRetriesExceeded(false);
    setShouldPoll(true);
    pollCount.current = 0;
    hasCalledOnAllComplete.current = false;
  }, []);

  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery<BulkActivitiesResponse>({
    // Use unique query key for modal polling to avoid conflicts with other hooks
    queryKey: BULK_ACTIVITIES_QUERY_KEYS.listWithSkipViewed(),
    // Pass skip_viewed: true to exclude already viewed activities in the modal
    queryFn: fetchBulkActivitiesWithSkipViewed,
    enabled: isTriggered,
    staleTime: 0,
    gcTime: 5000, // Keep cache for 5 seconds to prevent flickering during refetch
    retry: false, // Handle retries manually via polling
    // Keep previous data while refetching to prevent UI flickering
    placeholderData: (previousData) => previousData,
    // Fetch on mount to get initial data
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Derived data - compute these first so we can use them in effects
  const allActivities = useMemo(() => {
    if (!data) return [];
    const activities = [...(data.activities || []), ...(data.failed || [])];
    allActivitiesRef.current = activities; // Keep ref updated for callbacks
    return activities;
  }, [data]);

  const activeActivities = useMemo(() => {
    return allActivities.filter(
      (activity) =>
        activity.status === 'in_progress' || activity.status === 'pending'
    );
  }, [allActivities]);

  const hasActiveActivities = activeActivities.length > 0;
  const totalActivitiesCount = allActivities.length;
  if (hasActiveActivities) hasSeenWork.current = true;

  // Use ref to track if polling interval is active
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to handle polling logic - only depends on stable values
  useEffect(() => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Don't poll if not triggered or stopped
    if (!isTriggered || !shouldPoll) {
      return;
    }

    // ONLY poll if we have active activities
    // The grace period (recentlyTriggeredRef) should only apply to the first fetch,
    // not continue polling after all activities are completed
    if (hasActiveActivities) {
      pollingIntervalRef.current = setInterval(() => {
        pollCount.current += 1;
        if (pollCount.current > maxRetries) {
          setIsMaxRetriesExceeded(true);
          setShouldPoll(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }
        // Explicitly refetch the query (not just invalidate)
        queryClient.refetchQueries({
          queryKey: BULK_ACTIVITIES_QUERY_KEYS.listWithSkipViewed(),
        });
      }, pollInterval);
    } else if (totalActivitiesCount > 0) {
      // All activities are completed - stop polling and clear the grace period
      recentlyTriggeredRef.current = false;
      if (gracePeriodTimeoutRef.current) {
        clearTimeout(gracePeriodTimeoutRef.current);
        gracePeriodTimeoutRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    isTriggered,
    shouldPoll,
    hasActiveActivities,
    totalActivitiesCount,
    pollInterval,
    maxRetries,
    queryClient,
  ]);

  // Separate effect for completion callback - only runs when activities transition from active to complete
  useEffect(() => {
    // Don't fire if we're waiting for fresh data after a trigger
    if (waitingForFreshData.current) {
      return;
    }

    if (
      totalActivitiesCount > 0 &&
      !hasActiveActivities &&
      !hasCalledOnAllComplete.current
    ) {
      hasCalledOnAllComplete.current = true;
      if (onAllComplete && hasSeenWork.current) {
        onAllComplete(allActivitiesRef.current);
      }
      setShouldPoll(false);
    }
  }, [totalActivitiesCount, hasActiveActivities, onAllComplete]);

  // Resume polling when new active activities appear (e.g., new operation queued while others are completed)
  useEffect(() => {
    if (hasActiveActivities && !shouldPoll) {
      // New active activity detected, resume polling
      setShouldPoll(true);
      hasCalledOnAllComplete.current = false;
      pollCount.current = 0;
    }
  }, [hasActiveActivities, shouldPoll]);

  // Handle error callback
  useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  const completedActivities = useMemo(() => {
    return allActivities.filter((activity) => activity.status === 'completed');
  }, [allActivities]);

  const failedActivities = useMemo(() => {
    return allActivities.filter((activity) => activity.status === 'failed');
  }, [allActivities]);

  // isPolling is true when we're fetching or have active activities that need polling
  const isPolling =
    isTriggered && shouldPoll && (isLoading || hasActiveActivities);

  // Manual trigger function - refetches query to restart polling with skip_viewed=true
  const triggerFetch = useCallback(async () => {
    // Set flag BEFORE resetStates to prevent completion callback from firing on stale data
    waitingForFreshData.current = true;
    hasSeenWork.current = true;

    resetStates();
    setIsTriggered(true);

    // Set recently triggered flag to ensure polling happens for grace period
    // even if no active activities are found initially
    recentlyTriggeredRef.current = true;

    // Clear any existing grace period timeout
    if (gracePeriodTimeoutRef.current) {
      clearTimeout(gracePeriodTimeoutRef.current);
    }

    // Clear the recently triggered flag after 15 seconds (3 poll cycles)
    gracePeriodTimeoutRef.current = setTimeout(() => {
      recentlyTriggeredRef.current = false;
    }, 15000);

    // Use refetchQueries to explicitly trigger a refetch (not just invalidate)
    // This ensures the API call is made immediately
    await queryClient.refetchQueries({
      queryKey: BULK_ACTIVITIES_QUERY_KEYS.listWithSkipViewed(),
    });

    // Clear the waiting flag after fresh data has been fetched
    waitingForFreshData.current = false;
  }, [resetStates, queryClient]);

  // Stop polling
  const stopPolling = useCallback(() => {
    setShouldPoll(false);
    recentlyTriggeredRef.current = false;
    if (gracePeriodTimeoutRef.current) {
      clearTimeout(gracePeriodTimeoutRef.current);
      gracePeriodTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gracePeriodTimeoutRef.current) {
        clearTimeout(gracePeriodTimeoutRef.current);
      }
    };
  }, []);

  return {
    isLoading,
    isPolling,
    data,
    activeActivities,
    completedActivities,
    failedActivities,
    hasActiveActivities,
    isMaxRetriesExceeded,
    triggerFetch,
    stopPolling,
    error: error as Error | null,
  };
};
